import './useNotificationsHookConfig';
import { useReactiveVar } from '@apollo/client';
import { cloneDeep, uniqueId } from 'lodash';
import { ReactNode, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { renderNotification } from '../../cache/learner/homeReactiveVars';
import { jobPreferencesVar } from '../../cache/learner/jobPreferencesReactiveVar';
import { kanbanDataVar } from '../../cache/learner/kanbanReactiveVar';
import AtomTypography from '../../components/atoms/AtomTypography/AtomTypography';
import { styleConfigs } from '../../components/resources/styleConfigs';
import { NotificationContext } from '../../contexts/NotificationsContext';
import { AlertSeverity } from '../../contexts/reducer/notificationReducer';
import {
  IStudentNotification,
  NotificationType,
} from '../../operations/mutations/student/useMutationUpdateStudentSkillStatus/useMutationUpdateStudentSkillStatus';
import useJobPreferencesDataHook from '../jobPreferencesDataHook/useJobPreferencesDataHook';

export enum AddNotificationActionsEnum {
  ADD = 'ADD',
  DELETE = 'DELETE',
  MODIFY = 'MODIFY',
}

export enum LearningPathNotificationActionsEnum {
  COMPLETE = 'COMPLETE',
  REACTIVATE = 'REACTIVATE',
}

export interface IDatabaseNotification {
  id: number;
  date: string;
  skillName: string;
  type: NotificationType;
}

interface IAddSkillNotification {
  action: AddNotificationActionsEnum.ADD;
  data: {
    skillId: string;
    skillName: string;
    toStatus: string;
    fromStatus?: string;
    databaseNotification?: IDatabaseNotification;
  };
}

interface IDeleteSkillNotification {
  action: AddNotificationActionsEnum.DELETE;
  data: {
    skillId: string;
    skillName: string;
    fromStatus?: string;
    databaseNotification?: IDatabaseNotification;
  };
}

interface IModifySkillNotification {
  action: AddNotificationActionsEnum.MODIFY;
  data: {
    skillId: string;
    skillName: string;
    fromStatus: string;
    toStatus: string;
    databaseNotification?: IDatabaseNotification;
  };
}

export interface INotificationsHook {
  operations: {
    addNotification: (
      type: string,
      description: string | ReactNode,
      customTitle?: string,
      overrides?: {
        leftIcon?: ReactNode;
        variant?: AlertSeverity;
        linkText?: string;
        linkTo?: string;
        autoHideDuration?: number;
        onLinkClick?: () => void;
      },
    ) => string;
    addSkillNotification: (action: ISkillNotification) => void;
    addNotificationToVar: (notification: IStudentNotification) => void;
    errorCVNotification: (errorMessage: string) => void;
    addLearningPathNotification: (action: LearningPathNotificationActionsEnum) => void;
  };
}

type ISkillNotification = IAddSkillNotification | IDeleteSkillNotification | IModifySkillNotification;

type NotificationSeverity = 'error' | 'warning' | 'success' | 'info';

const NOTIFICATION_TYPE_CONFIG: Record<
  string,
  {
    titleKey: string;
    severity: NotificationSeverity;
    leftIcon?: ReactNode;
    variant?: AlertSeverity;
    linkText?: string;
    linkTo?: string;
  }
> = {
  danger: { titleKey: 'DANGER_NOTIFICATION', severity: 'error' },
  congratulation: { titleKey: 'CONGRATULATION_NOTIFICATION', severity: 'success' },
  thumbsUp: { titleKey: 'CONGRATULATION_NOTIFICATION', severity: 'success' },
  occupationSelected: { titleKey: 'OCCUPATION_SELECTED', severity: 'success' },
  warning: { titleKey: 'WARNING_NOTIFICATION', severity: 'warning' },
  lookout: { titleKey: 'LOKOUT_NOTIFICATION', severity: 'info' },
  bulb: { titleKey: 'LOKOUT_NOTIFICATION', severity: 'info' },
  removed: { titleKey: 'REMOVED_NOTIFICATION', severity: 'info' },
  emailSend: { titleKey: 'SUCCESS_NOTIFICATION', severity: 'success' },
  emailResend: { titleKey: 'SUCCESS_NOTIFICATION', severity: 'success' },
  info: { titleKey: 'SUCCESS_NOTIFICATION', severity: 'info' },
};

const DEFAULT_NOTIFICATION = { titleKey: 'SUCCESS_NOTIFICATION', severity: 'success' as NotificationSeverity };

const useNotificationsHook = (): INotificationsHook => {
  const { notificationOpen } = useContext(NotificationContext);
  const { t } = useTranslation('useNotificationsHook');
  const { operations } = useJobPreferencesDataHook(jobPreferencesVar, kanbanDataVar);
  const allNotifications = useReactiveVar(renderNotification);

  const addNotificationToVar = (notification: IStudentNotification): void => {
    const copyOfNotification = cloneDeep(allNotifications);

    copyOfNotification.newNotifications.unshift(notification);
    renderNotification(copyOfNotification);
  };

  const handleOccupationsNumber = (
    action: ISkillNotification,
    forOneOccupation: string,
    forOccupations: string,
  ): string => (operations.getOccupationChanges(action.data.skillId) === '1' ? forOneOccupation : forOccupations);

  const skillNotificationAdd = (action: IAddSkillNotification): void => {
    if (action.data.toStatus === 'completed') {
      addNotification(
        'congratulation',
        t(handleOccupationsNumber(action, 'CONGRATULATIONS_1_OCCUPATION', 'CONGRATULATIONS'), {
          numberOccupations: operations.getOccupationChanges(action.data.skillId),
        }),
      );
    } else if (action.data.fromStatus === 'completed') {
      addNotification(
        'lookout',
        t(
          handleOccupationsNumber(
            action,
            'LOOKOUT_NOTIFICATION_DESCRIPTION_1_OCCUPATION',
            'LOOKOUT_NOTIFICATION_DESCRIPTION',
          ),
          { numberOccupations: operations.getOccupationChanges(action.data.skillId) },
        ),
      );
    } else {
      addNotification(
        'success',
        <AtomTypography>
          {t('YOU_ADDED')}
          <AtomTypography
            component="span"
            display="inline"
            fontWeight={700}
            color="inherit"
            sx={{ margin: `${styleConfigs.marginPadding.empty} ${styleConfigs.marginPadding.custom5}px` }}
          >
            {action.data.skillName}
          </AtomTypography>
          {action.data.toStatus === 'learning' ? t('TO_LEARNING_SKILL_TRACKER') : t('TO_LEARN_SKILL_TRACKER')}
        </AtomTypography>,
        t('ADD_SKILL_NOTIFICATION', { skill: action.data.skillName }),
      );
    }
    if (action.data.databaseNotification) {
      addNotificationToVar({
        id: action.data.databaseNotification.id,
        type: action.data.databaseNotification.type,
        skillName: action.data.databaseNotification.skillName,
        date: action.data.databaseNotification.date,
        occupationName: '',
        skillStatus: action.data.toStatus,
      });
    }
  };

  const notifyToVar = (action: ISkillNotification, withSkillStatus: boolean): void => {
    if (action.data.databaseNotification) {
      let skillStatus = '';

      if (withSkillStatus && 'toStatus' in action.data) {
        const { toStatus } = action.data;

        skillStatus = toStatus;
      }
      addNotificationToVar({
        id: action.data.databaseNotification.id,
        type: action.data.databaseNotification.type,
        skillName: action.data.databaseNotification.skillName,
        date: action.data.databaseNotification.date,
        occupationName: '',
        skillStatus,
      });
    }
  };

  const getTitle = (type: string): string => (type === 'success' ? t('YOU_MOVED') : t('YOU_DELETED'));

  const getCustomTitle = (type: string, skillName: string): string =>
    type === 'success'
      ? t('DRAG_SKILL_NOTIFICATION', { skill: skillName })
      : t('REMOVED_SKILL_NOTIFICATION', { skill: skillName });

  const getSkillStatus = (type: string, status: string | undefined): string => {
    if (type === 'removed') {
      return status === 'learning' ? t('FROM_LEARNING_SKILL_TRACKER') : t('FROM_LEARN_SKILL_TRACKER');
    }

    return status === 'learning' ? t('TO_LEARNING_SKILL_TRACKER') : t('TO_LEARN_SKILL_TRACKER');
  };

  const addNotificationWithTypo = (type: string, status: string | undefined, skillName: string): void => {
    const title = getTitle(type);
    const customTitle = getCustomTitle(type, skillName);
    const skillStatus = getSkillStatus(type, status);

    addNotification(
      type,
      <AtomTypography>
        {title}
        <AtomTypography
          component="span"
          display="inline"
          fontWeight={700}
          color="inherit"
          sx={{ margin: `${styleConfigs.marginPadding.empty} ${styleConfigs.marginPadding.custom5}px` }}
        >
          {skillName}
        </AtomTypography>
        {skillStatus}
      </AtomTypography>,
      customTitle,
    );
  };

  const skillNotificationDelete = (action: IDeleteSkillNotification): void => {
    if (action.data.fromStatus === 'completed') {
      addNotification(
        'lookout',
        t(
          handleOccupationsNumber(
            action,
            'LOOKOUT_NOTIFICATION_DESCRIPTION_1_OCCUPATION',
            'LOOKOUT_NOTIFICATION_DESCRIPTION',
          ),
          { numberOccupations: operations.getOccupationChanges(action.data.skillId) },
        ),
      );
    } else {
      addNotificationWithTypo('removed', action.data.fromStatus, action.data.skillName);

      return;
    }
    notifyToVar(action, false);
  };

  const skillNotificationModify = (action: IModifySkillNotification): void => {
    if (action.data.fromStatus === 'completed') {
      addNotification(
        'lookout',
        t(
          handleOccupationsNumber(
            action,
            'LOOKOUT_NOTIFICATION_DESCRIPTION_1_OCCUPATION',
            'LOOKOUT_NOTIFICATION_DESCRIPTION',
          ),
          { numberOccupations: operations.getOccupationChanges(action.data.skillId) },
        ),
      );
      notifyToVar(action, true);

      return;
    }
    if (action.data.toStatus === 'completed') {
      addNotification(
        'congratulation',
        t(handleOccupationsNumber(action, 'CONGRATULATIONS_1_OCCUPATION', 'CONGRATULATIONS'), {
          numberOccupations: operations.getOccupationChanges(action.data.skillId),
        }),
      );
      notifyToVar(action, true);

      return;
    }
    addNotificationWithTypo('success', action.data.toStatus, action.data.skillName);
  };

  const addSkillNotification = (action: ISkillNotification): void => {
    switch (action.action) {
      case AddNotificationActionsEnum.ADD:
        skillNotificationAdd(action);
        break;

      case AddNotificationActionsEnum.DELETE:
        skillNotificationDelete(action);
        break;

      case AddNotificationActionsEnum.MODIFY:
        skillNotificationModify(action);
        break;

      default:
        addNotification('danger', 'Unsupported Action');
    }
  };

  const addNotification = (
    type: string,
    description: string | ReactNode,
    customTitle?: string,
    overrides?: {
      leftIcon?: ReactNode;
      variant?: AlertSeverity;
      linkText?: string;
      linkTo?: string;
      autoHideDuration?: number;
      onLinkClick?: () => void;
    },
  ): string => {
    const { titleKey, severity, leftIcon, variant, linkText, linkTo } =
      NOTIFICATION_TYPE_CONFIG[type] ?? DEFAULT_NOTIFICATION;
    const title = t(titleKey);

    const notificationId = uniqueId('notification_');

    notificationOpen({
      listNotifications: [
        {
          id: notificationId,
          title: customTitle ?? title,
          description,
          severity,
          leftIcon: overrides?.leftIcon ?? leftIcon,
          variant: overrides?.variant ?? variant,
          linkText: overrides?.linkText ?? linkText,
          linkTo: overrides?.linkTo ?? linkTo,
          autoClose: true,
          autoHideDuration: overrides?.autoHideDuration,
          onLinkClick: overrides?.onLinkClick,
        },
      ],
    });

    return notificationId;
  };

  const addLearningPathNotification = (action: LearningPathNotificationActionsEnum): void => {
    switch (action) {
      case LearningPathNotificationActionsEnum.COMPLETE:
        addNotification(
          'thumbsUp',
          <AtomTypography>
            {t('COMPLETE_LEARNING_PATH_DESCRIPTION')}
            <AtomTypography
              component="span"
              display="inline"
              fontWeight={700}
              color="inherit"
              sx={{ margin: `${styleConfigs.marginPadding.empty} ${styleConfigs.marginPadding.custom5}px` }}
            >
              {t('LP_COMPLETE_STATUS')}
            </AtomTypography>
          </AtomTypography>,
          t('COMPLETE_LP_TITLE'),
        );
        break;

      case LearningPathNotificationActionsEnum.REACTIVATE:
        addNotification(
          'bulb',
          <AtomTypography>{t('REACTIVATE_LP_DESCRIPTION')}</AtomTypography>,
          t('REACTIVATE_LP_TITLE'),
        );
        break;

      default:
        addNotification('danger', 'Unsupported Action');
    }
  };

  const errorCVNotification = (errorMessage: string): void => {
    const message = errorMessage.toString().split(':')[0];

    switch (message) {
      case 'Error code 001:':
        addNotification('warning', t('CV_MAX_SIZE', { size: '5' }));
        break;

      case 'Error code 002:':
        addNotification('warning', t('CV_MAX_SIZE', { size: '5' }));
        break;

      case 'Error code 452':
        addNotification('warning', t('CV_ERROR_452'));
        break;

      case 'Error code 453':
        addNotification('warning', t('CV_VALID_FORMAT'));
        break;

      case 'Error code 454':
        addNotification('warning', t('CV_ERROR_454'));
        break;

      case 'Error code 512':
        addNotification('warning', t('CV_ERROR_512'));
        break;

      case 'Error code 513':
        addNotification('warning', t('CV_ERROR_513'));
        break;

      default:
        addNotification('warning', t('CV_UPLOAD_ERROR'));
        break;
    }
  };

  return {
    operations: {
      addNotification,
      addSkillNotification,
      addNotificationToVar,
      errorCVNotification,
      addLearningPathNotification,
    },
  };
};

export default useNotificationsHook;
